/**
 * O documento do exame no centro da mesa única (E202.F1, GUS-145, ADR-0145 §1 e §5), no lugar do PDF.
 *
 * O exame (ou, em PARTE, o relatório de risco: a troca fica no alto) aparece como o papel o imprime,
 * seção por seção, com as imagens e as tabelas. É a prévia de trabalho: a prévia definitiva, o PDF
 * que a preparação guardou, fica no painel ao lado ("Prévia (PDF)"). A seção da conclusão e a da
 * análise facial levam "Alterar na mesa" para a área que as registra com fundamento (ADR-0145 §8).
 *
 * Primeira leitura com esqueleto; falha com "Tentar de novo"; releitura com o documento anterior à
 * vista e "Atualizando o documento…".
 *
 * E202.F2 (ADR-0145 §3–§9): com a edição ligada, o texto de redação abre o editor no próprio bloco;
 * cada seção que aceita nota tem o convite para escrevê-la; o mapa do risco edita os campos de cada
 * risco; no fim, as edições de uma versão anterior e as que o texto atual não tem mais. A saída
 * assinada é só leitura, com o aviso. `?trecho=` abre o trecho apontado.
 *
 * E203.F2 (ADR-0146 §1): com a edição ligada, uma linha no alto diz que qualquer trecho se edita
 * pelo clique; as tabelas e o texto protegido abrem o painel do dado (`AbrirDadoContexto`).
 * E203.F3 (ADR-0146 §5): depois do dado novo, o aviso diz quantas edições seguiram e quantas mudaram.
 *
 * E205.F2 (ADR-0147 §2): na mesa, uma seção por vez (`porSecao`), com a navegação no alto e no fim; a
 * troca de saída, a versão do texto e "Conferir e assinar" foram para a barra da aba e o cabeçalho; a
 * linha "clique em qualquer trecho" saiu (a dica fica no `title` de cada bloco clicável).
 */
import { useMemo } from 'react';

import { FalhaComSaida, Seg, Skeleton } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type { SaidaDaEdicao } from '@/lib/correcaoDoRascunho';
import { trechosPorId, type DocumentoDaSaidaData } from '@/lib/documentoDaSaida';

import { AvisoDaAnaliseNova } from './AvisoDaAnaliseNova';
import type { ContextoDasPartes } from './BlocoDoDocumento';
import { EdicoesForaDoDocumento } from './EdicoesForaDoDocumento';
import { Folha, type PorSecao } from './FolhaDoDocumento';
import { TrechoApontado } from './TrechoApontado';
import { TravaDoPerfil } from './TravaDoPerfil';
import type { LeituraDoDocumento } from './useDocumentoDaSaida';
import type { EdicaoNoDocumento } from './useEdicaoNoDocumento';

const t = ptBR.documentoDaMesa;

const SAIDAS = [
  { value: 'exame', label: t.saidas.exame, testId: 'documento-saida-exame' },
  { value: 'riscos', label: t.saidas.riscos, testId: 'documento-saida-riscos' },
] as const;

/** A troca entre o exame e o relatório de risco (PARTE). E205.F2: na mesa, na barra da aba Exame. */
export function TrocaDeSaida({
  saida,
  onSaida,
}: {
  saida: SaidaDaEdicao;
  onSaida: (saida: SaidaDaEdicao) => void;
}): JSX.Element {
  return <Seg options={SAIDAS} value={saida} onChange={onSaida} label={t.saidas.rotulo} />;
}

function Papel({
  documento,
  edicao,
  porSecao,
  onAtualizar,
}: {
  documento: DocumentoDaSaidaData;
  edicao: EdicaoNoDocumento | null;
  porSecao: PorSecao | null;
  onAtualizar: () => void;
}): JSX.Element {
  const trechos = useMemo(() => trechosPorId(documento), [documento]);
  const contexto: ContextoDasPartes = {
    trechos,
    avaliacaoId: documento.avaliacao_id,
    edicao: documento.assinado ? null : edicao,
  };
  if (!documento.aplicavel && documento.trava_do_perfil) {
    // E222.F1: a trava do perfil normativo diz o que falta e abre o caminho no painel
    return <TravaDoPerfil trava={documento.trava_do_perfil} onAtualizar={onAtualizar} />;
  }
  if (!documento.aplicavel) {
    const motivo = documento.motivo ?? 'indisponivel';
    return (
      <p className="border border-border bg-surface-2 p-sc-4 text-body-sm text-fg-muted">
        {t.semDocumento[motivo]}
      </p>
    );
  }
  return (
    <>
      {documento.assinado ? (
        <p className="mb-sc-3 text-body-sm text-fg-muted" data-testid="documento-assinado">
          {t.assinado}
        </p>
      ) : null}
      {contexto.edicao !== null ? <AvisoDaAnaliseNova documento={documento} /> : null}
      {porSecao === null ? (
        <Folha secoes={documento.secoes} contexto={contexto} sozinha={false} />
      ) : (
        <div
          id={porSecao.ativa ?? undefined}
          className="scroll-mt-sc-6 space-y-sc-3"
          data-testid="documento-secao-ativa"
        >
          {porSecao.alto}
          <Folha
            secoes={documento.secoes.filter((s) => s.ancora === porSecao.ativa)}
            contexto={contexto}
            sozinha
          />
          {porSecao.fim}
        </div>
      )}
      <EdicoesForaDoDocumento documento={documento} edicao={contexto.edicao} />
    </>
  );
}

function Esqueleto(): JSX.Element {
  return (
    <div className="space-y-sc-3" data-testid="documento-esqueleto" aria-label={t.carregando}>
      <Skeleton className="h-16 w-full" />
      <Skeleton className="h-4 w-3/4" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-5/6" />
      <Skeleton className="h-40 w-full" />
    </div>
  );
}

export interface DocumentoDaMesaProps {
  leitura: LeituraDoDocumento;
  saida: SaidaDaEdicao;
  /** O caso tem relatório de risco (PARTE): a troca de saída aparece. */
  comRisco: boolean;
  onSaida: (saida: SaidaDaEdicao) => void;
  /** A edição no documento (E202.F2); sem ela, o documento é só leitura. */
  edicao?: EdicaoNoDocumento | null;
  /** `?trecho=`: o texto que a conferência por regras apontou (E184.F4). */
  apontado?: string | null;
  /** E205.F2: na mesa, só a seção à vista, com a navegação; sem ele, o documento inteiro. */
  porSecao?: PorSecao | null;
}

function Gravacao({ edicao }: { edicao: EdicaoNoDocumento | null }): JSX.Element | null {
  if (edicao?.gravacao !== 'salva') return null;
  return (
    <p role="status" className="text-body-sm text-success-700" data-testid="documento-gravacao">
      {t.edicao.salva}
    </p>
  );
}

/** A leitura em curso, a espera da primeira (E206.F3), a falha com o motivo e o esqueleto. */
function EstadoDaLeitura({ leitura }: { leitura: DocumentoDaMesaProps['leitura'] }): JSX.Element {
  const { dados, carregando, falhou, tentarDeNovo } = leitura;
  const esperando = leitura.esperando === true;
  return (
    <>
      {carregando && dados !== null ? (
        <p role="status" className="text-body-sm text-fg-muted" data-testid="documento-atualizando">
          {t.atualizando}
        </p>
      ) : null}
      {falhou ? (
        <FalhaComSaida
          mensagem={leitura.falha === 'tempo' ? t.erroTempo : t.erro}
          onTentarDeNovo={tentarDeNovo}
          tentando={carregando}
          data-testid="documento-falha"
        />
      ) : null}
      {dados === null && carregando && esperando ? (
        <p role="status" className="text-body-sm text-fg-muted" data-testid="documento-esperando">
          {t.esperando}
        </p>
      ) : null}
      {dados === null && carregando ? <Esqueleto /> : null}
    </>
  );
}

export function DocumentoDaMesa({
  leitura,
  saida,
  comRisco,
  onSaida,
  edicao = null,
  apontado = null,
  porSecao = null,
}: DocumentoDaMesaProps): JSX.Element {
  const { dados } = leitura;
  return (
    <div className="space-y-sc-3" data-testid="documento-da-mesa">
      {comRisco ? <TrocaDeSaida saida={saida} onSaida={onSaida} /> : null}
      <TrechoApontado
        key={saida}
        procurado={apontado}
        documento={dados}
        edicao={dados?.assinado ? null : edicao}
        saida={saida}
      />
      <Gravacao edicao={edicao} />
      <EstadoDaLeitura leitura={leitura} />
      {dados !== null ? (
        <Papel
          documento={dados}
          edicao={edicao}
          porSecao={porSecao}
          onAtualizar={leitura.tentarDeNovo}
        />
      ) : null}
    </div>
  );
}
